import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Container, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../../Navbar';
import PageTitle from '../../../components/PageTitle';
import FlashcardSideEditor from './FlashcardSideEditor';
import { authHeaders, flashcardsFetch, isCardSideEmpty } from './flashcardsUtils';
import '../guides.css';

function emptyRow() {
  return { key: `new-${Date.now()}-${Math.random()}`, id: null, frontHtml: '', backHtml: '' };
}

function FlashcardEditorRow({ index, row, onUpdate, onRemove }) {
  const frontWrapRef = useRef(null);
  const backWrapRef = useRef(null);

  const syncHeights = useCallback(() => {
    const front = frontWrapRef.current;
    const back = backWrapRef.current;
    if (!front || !back) return;

    // On mobile, Front/Back stack — don't force equal heights
    const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
    if (isMobile) {
      front.style.minHeight = '';
      back.style.minHeight = '';
      return;
    }

    front.style.minHeight = '0px';
    back.style.minHeight = '0px';

    const next = Math.max(
      front.scrollHeight,
      back.scrollHeight,
      44
    );
    const px = `${next}px`;
    front.style.minHeight = px;
    back.style.minHeight = px;
  }, []);

  useLayoutEffect(() => {
    syncHeights();
  }, [row.frontHtml, row.backHtml, syncHeights]);

  useEffect(() => {
    const front = frontWrapRef.current;
    const back = backWrapRef.current;
    if (!front || !back) return undefined;

    const onSideResize = () => {
      window.requestAnimationFrame(syncHeights);
    };

    front.addEventListener('fc-side-resize', onSideResize);
    back.addEventListener('fc-side-resize', onSideResize);
    window.addEventListener('resize', onSideResize);

    let frame = 0;
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(syncHeights);
      })
      : null;
    if (ro) {
      ro.observe(front);
      ro.observe(back);
    }

    return () => {
      front.removeEventListener('fc-side-resize', onSideResize);
      back.removeEventListener('fc-side-resize', onSideResize);
      window.removeEventListener('resize', onSideResize);
      cancelAnimationFrame(frame);
      ro?.disconnect();
    };
  }, [syncHeights]);

  return (
    <div className="fc-editor-row">
      <div className="fc-editor-col-num">
        <span className="fc-editor-index">{index + 1}</span>
      </div>
      <div className="fc-editor-col-side" data-label="Front" ref={frontWrapRef}>
        <FlashcardSideEditor
          value={row.frontHtml}
          onChange={(frontHtml) => onUpdate({ frontHtml })}
          placeholder="Questions/Term"
        />
      </div>
      <div className="fc-editor-col-side" data-label="Back" ref={backWrapRef}>
        <FlashcardSideEditor
          value={row.backHtml}
          onChange={(backHtml) => onUpdate({ backHtml })}
          placeholder="Answer/definition"
        />
      </div>
      <div className="fc-editor-col-actions">
        <button
          type="button"
          className="fc-editor-delete-btn"
          title="Remove card"
          aria-label="Remove card"
          onClick={onRemove}
        >
          <i className="bi bi-trash" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default function FlashcardSetEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [setMeta, setSetMeta] = useState(null);
  const [title, setTitle] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flashcardsFetch(`/sets/${id}`, { headers: authHeaders() });
      if (!data.canEdit) {
        toast.info('You can study this set but only the owner can edit cards.');
        navigate(`/guides/flashcards/sets/${id}`, { replace: true });
        return;
      }
      setSetMeta(data);
      setTitle(data.title || '');
      const cards = Array.isArray(data.cards) ? data.cards : [];
      if (cards.length) {
        setRows(cards.map((c) => ({
          key: `card-${c.id}`,
          id: c.id,
          frontHtml: c.frontHtml || '',
          backHtml: c.backHtml || ''
        })));
      } else {
        setRows([emptyRow()]);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load set');
      navigate('/guides/flashcards');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addCard = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const removeCard = (key) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((r) => r.key !== key)));
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      await flashcardsFetch(`/sets/${id}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ title: title.trim() })
      });

      const cardsPayload = rows
        .filter((r) => !isCardSideEmpty(r.frontHtml) || !isCardSideEmpty(r.backHtml))
        .map((r) => ({
          id: r.id || undefined,
          frontHtml: r.frontHtml || '',
          backHtml: r.backHtml || ''
        }));

      const saved = await flashcardsFetch(`/sets/${id}/cards`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ cards: cardsPayload })
      });

      const cards = Array.isArray(saved.cards) ? saved.cards : [];
      if (cards.length) {
        setRows(cards.map((c) => ({
          key: `card-${c.id}`,
          id: c.id,
          frontHtml: c.frontHtml || '',
          backHtml: c.backHtml || ''
        })));
      } else {
        setRows([emptyRow()]);
      }
      setSetMeta((m) => (m ? { ...m, title: title.trim(), cardCount: cards.length } : m));
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const backTarget = setMeta?.section?.deck?.id
    ? `/guides/flashcards/decks/${setMeta.section.deck.id}`
    : `/guides/flashcards/sets/${id}`;

  if (loading) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Edit set" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={title || 'Edit set'} />
      <PageTitle title={title || 'Edit flashcard set'} />
      <div className="bg-white border-bottom guides-topic-editor-topbar">
        <Container fluid="lg">
          <div className="d-flex justify-content-between align-items-center py-2 gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-3 min-w-0 flex-grow-1">
              <Button
                size="sm"
                variant="light-solid"
                className="guides-icon-btn"
                onClick={() => navigate(backTarget)}
              >
                <i className="bi bi-chevron-left" />
              </Button>
              <Form.Control
                className="fc-set-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Flashcard set title"
              />
            </div>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => navigate(`/guides/flashcards/sets/${id}`)}
              >
                View set
              </Button>
              <Button size="sm" variant="primary" onClick={save} disabled={saving}>
                {saving ? <Spinner size="sm" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <div className="guides-read-scroll pt-4 pb-5 pe-0">
          <div className="fc-editor-table">
            <div className="fc-editor-header">
              <div className="fc-editor-col-num">#</div>
              <div className="fc-editor-col-side fc-editor-col-front">Front</div>
              <div className="fc-editor-col-side fc-editor-col-back">Back</div>
              <div className="fc-editor-col-side fc-editor-col-frontback" aria-hidden="true">Front/back</div>
              <div className="fc-editor-col-actions">Delete</div>
            </div>
            {rows.map((row, index) => (
              <FlashcardEditorRow
                key={row.key}
                index={index}
                row={row}
                onUpdate={(patch) => updateRow(row.key, patch)}
                onRemove={() => removeCard(row.key)}
              />
            ))}
          </div>
          <Button variant="light-lite primary-light" className="fc-editor-add-btn w-100" onClick={addCard}>
            <i className="bi bi-plus-lg" /> Add new card
          </Button>
        </div>
      </Container>
    </div>
  );
}
